import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, Button, Space, Tag, message, Divider, Typography, Card, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, MailOutlined, BellOutlined } from '@ant-design/icons';
import { useAuth } from '../Context';
import { 
  obtenerConfiguracionNotificaciones, 
  actualizarConfiguracionNotificaciones 
} from '../../servicios/configuracion/notificacionesApi';

const { Title, Text, Paragraph } = Typography;

const ConfiguracionNotificacionesModal = ({ visible, onClose, sucursal }) => {
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [configuracion, setConfiguracion] = useState({
    correos: [],
    activo: false
  });
  const [nuevoCorreo, setNuevoCorreo] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && sucursal) {
      cargarConfiguracion();
    }
  }, [visible, sucursal]);

  const cargarConfiguracion = async () => {
    setLoading(true);
    try {
      const config = await obtenerConfiguracionNotificaciones(sucursal);
      setConfiguracion({
        correos: config.correos || [],
        activo: config.activo || false
      });
      
      // Actualizar el formulario con los datos cargados
      form.setFieldsValue({
        activo: config.activo || false
      });
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      message.error('Error al cargar la configuración de notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const validarEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const agregarCorreo = () => {
    if (!nuevoCorreo.trim()) {
      message.warning('Por favor ingrese un correo electrónico');
      return;
    }

    if (!validarEmail(nuevoCorreo)) {
      message.error('Por favor ingrese un correo electrónico válido');
      return;
    }

    if (configuracion.correos.includes(nuevoCorreo.toLowerCase())) {
      message.warning('Este correo ya está agregado');
      return;
    }

    setConfiguracion(prev => ({
      ...prev,
      correos: [...prev.correos, nuevoCorreo.toLowerCase()]
    }));
    setNuevoCorreo('');
  };

  const eliminarCorreo = (correoAEliminar) => {
    setConfiguracion(prev => ({
      ...prev,
      correos: prev.correos.filter(correo => correo !== correoAEliminar)
    }));
  };

  const handleActivoChange = (checked) => {
    setConfiguracion(prev => ({
      ...prev,
      activo: checked
    }));
  };

  const handleGuardar = async () => {
    if (configuracion.correos.length === 0 && configuracion.activo) {
      message.error('Debe agregar al menos un correo para activar las notificaciones');
      return;
    }

    setGuardando(true);
    try {
      await actualizarConfiguracionNotificaciones({
        sucursal: sucursal,
        correos: configuracion.correos,
        activo: configuracion.activo
      });
      
      message.success('Configuración guardada exitosamente');
      onClose();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      message.error('Error al guardar la configuración');
    } finally {
      setGuardando(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      agregarCorreo();
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BellOutlined style={{ color: '#1890ff' }} />
          <span>Configuración de Notificaciones</span>
          <Tag color="blue">{sucursal}</Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={null}
      destroyOnClose
    >
      <div style={{ marginTop: '16px' }}>
        <Alert
          message="Configuración de Notificaciones por Correo"
          description="Configure los correos que recibirán notificaciones automáticas cuando se cree una nueva solicitud en esta sucursal."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Card size="small" style={{ marginBottom: '20px' }}>
          <Form form={form} layout="vertical">
            <Form.Item
              label={
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  <BellOutlined /> Estado de las Notificaciones
                </span>
              }
              name="activo"
              valuePropName="checked"
            >
              <Switch
                checked={configuracion.activo}
                onChange={handleActivoChange}
                checkedChildren="Activo"
                unCheckedChildren="Inactivo"
                loading={loading}
              />
            </Form.Item>
            
            {configuracion.activo && (
              <Text type="success" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                ✅ Se enviarán notificaciones automáticas a los correos configurados
              </Text>
            )}
            
            {!configuracion.activo && (
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                ⚠️ Las notificaciones automáticas están desactivadas
              </Text>
            )}
          </Form>
        </Card>

        <Card 
          size="small" 
          title={
            <span style={{ color: '#1890ff' }}>
              <MailOutlined /> Correos de Notificación
            </span>
          }
          style={{ marginBottom: '20px' }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="ejemplo@correo.com"
                value={nuevoCorreo}
                onChange={(e) => setNuevoCorreo(e.target.value)}
                onKeyPress={handleKeyPress}
                prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                disabled={loading}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={agregarCorreo}
                disabled={loading || !nuevoCorreo.trim()}
              >
                Agregar
              </Button>
            </Space.Compact>
          </div>

          <div style={{ minHeight: '60px' }}>
            {configuracion.correos.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {configuracion.correos.map((correo, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => eliminarCorreo(correo)}
                    color="blue"
                    style={{ 
                      marginBottom: '4px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <MailOutlined style={{ fontSize: '12px' }} />
                    {correo}
                  </Tag>
                ))}
              </div>
            ) : (
              <div 
                style={{ 
                  textAlign: 'center', 
                  color: '#bfbfbf',
                  padding: '20px',
                  border: '2px dashed #d9d9d9',
                  borderRadius: '6px'
                }}
              >
                <MailOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                <br />
                No hay correos configurados
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Agregue al menos un correo para recibir notificaciones
                </Text>
              </div>
            )}
          </div>

          {configuracion.correos.length > 0 && (
            <Paragraph style={{ marginTop: '12px', marginBottom: '0', fontSize: '12px' }}>
              <Text type="secondary">
                📧 {configuracion.correos.length} correo(s) configurado(s) para recibir notificaciones
              </Text>
            </Paragraph>
          )}
        </Card>

        <div style={{ textAlign: 'right', marginTop: '24px' }}>
          <Space>
            <Button onClick={onClose} disabled={guardando}>
              Cancelar
            </Button>
            <Button 
              type="primary" 
              onClick={handleGuardar}
              loading={guardando}
              icon={<BellOutlined />}
            >
              Guardar Configuración
            </Button>
          </Space>
        </div>
      </div>
    </Modal>
  );
};

export default ConfiguracionNotificacionesModal;